import { useState, useCallback, useRef } from 'react'
import { resourceService } from '../services/resourceService'
import type { ResourceFileUrl } from '../types'

const urlCache = new Map<string, ResourceFileUrl>()

function isCacheValid(entry: ResourceFileUrl): boolean {
  return new Date(entry.expiresAt) > new Date(Date.now() + 60_000)
}

export function useResourceUrl() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inflightRef = useRef<Map<string, Promise<ResourceFileUrl>>>(new Map())

  const getUrl = useCallback(async (id: string): Promise<string | null> => {
    const cached = urlCache.get(id)
    if (cached && isCacheValid(cached)) return cached.url

    const inflight = inflightRef.current.get(id)
    if (inflight) {
      const result = await inflight
      return result.url
    }

    setIsLoading(true)
    setError(null)

    const promise = resourceService.getFileUrl(id)
    inflightRef.current.set(id, promise)

    try {
      const result = await promise
      urlCache.set(id, result)
      return result.url
    } catch {
      setError('No se pudo obtener la URL del recurso.')
      return null
    } finally {
      inflightRef.current.delete(id)
      setIsLoading(false)
    }
  }, [])

  return { getUrl, isLoading, error }
}
